function toHHMM(unixSeconds) {
  const d = new Date(unixSeconds * 1000);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function toEpochMilliseconds(unixSeconds) {
  return unixSeconds * 1000;
}


/**
 * Build time series per groupKey.
 * mode: "flow" | "cumulative"
 *
 * Returns:
 * {
 *   [groupKey]: { starts, labels, ingress, egress }
 * }
 */
export function buildIngressEgressSeriesByGroup(flowRows, uuidToGroupKey, mode = "flow") {
  // groupKey -> Map(start -> { in, out })
  const groupBuckets = new Map();

  for (const r of flowRows) {
    const uuid = r.uuid || r?._id?.uuid;
    if (!uuid) continue;

    const groupKey = uuidToGroupKey.get(uuid);
    if (!groupKey) continue; // filtered out

    const start = Number(r.start);
    if (!Number.isFinite(start)) continue;

    const inVal = Number(r.in ?? 0);
    const outVal = Number(r.out ?? 0);

    if (!groupBuckets.has(groupKey)) groupBuckets.set(groupKey, new Map());
    const buckets = groupBuckets.get(groupKey);

    if (!buckets.has(start)) buckets.set(start, { in: 0, out: 0 });
    const b = buckets.get(start);

    b.in += inVal;
    b.out += outVal;
  }

  const result = {};
  for (const [groupKey, buckets] of groupBuckets.entries()) {
    const starts = Array.from(buckets.keys()).sort((a, b) => a - b);
    const labels = starts.map(toEpochMilliseconds);

    let ingress = starts.map((s) => buckets.get(s).in);
    let egress = starts.map((s) => buckets.get(s).out);

    if (mode === "cumulative") {
      ingress = cumulative(ingress);
      egress = cumulative(egress);
    }

    result[groupKey] = { starts, labels, ingress, egress };
  }

  return result;
}

/**
 * Aligns per-group series onto a single shared timeline (union of all starts),
 * so we can plot multiple groups on one chart.
 *
 * Returns:
 * {
 *   labels: string[],
 *   starts: number[],
 *   series: { [groupKey]: { ingress: number[], egress: number[] } }
 * }
 */
export function alignGroupsToUnifiedTimeline(seriesByGroup) {
  if (!seriesByGroup || Object.keys(seriesByGroup).length === 0) {
    return { labels: [], starts: [], series: {} };
  }

  const allStartsSet = new Set();
  for (const s of Object.values(seriesByGroup)) {
    (s?.starts ?? []).forEach((t) => allStartsSet.add(t));
  }

  const starts = Array.from(allStartsSet).sort((a, b) => a - b);
  const labels = starts.map(toEpochMilliseconds);

  const series = {};
  for (const [groupKey, s] of Object.entries(seriesByGroup)) {
    const idx = new Map((s?.starts ?? []).map((t, i) => [t, i]));

    const ingress = starts.map((t) => {
      const i = idx.get(t);
      return i == null ? 0 : Number(s.ingress?.[i] ?? 0);
    });

    const egress = starts.map((t) => {
      const i = idx.get(t);
      return i == null ? 0 : Number(s.egress?.[i] ?? 0);
    });

    series[groupKey] = { ingress, egress };
  }

  return { labels, starts, series };
}

function cumulative(arr) {
  let s = 0;
  return arr.map(v => (s += Number(v ?? 0)));
}

// occupancy = cumulative(in - out)
export function toOccupancySeries({ ingress, egress }, { zeroEnd = false, clamp0 = true } = {}) {
  const deltas = ingress.map((v, i) => Number(v ?? 0) - Number(egress?.[i] ?? 0));
  let occ = cumulative(deltas);

  // Optional: prevent negative occupancy due to noise
  if (clamp0) occ = occ.map(v => Math.max(0, v));

  // Optional: force the series to end at 0 (baseline correction)
  if (zeroEnd && occ.length) {
    const offset = occ[occ.length - 1];     // where we ended up
    occ = occ.map(v => v - offset);         // shift whole curve so last point = 0
    if (clamp0) occ = occ.map(v => Math.max(0, v));
  }

  return occ;
}

export function computeOccupancyFromFlow(ingressFlow, egressFlow, {
  clamp0 = true,
  zeroEnd = false
} = {}) {
  const n = Math.max(ingressFlow?.length ?? 0, egressFlow?.length ?? 0);
  const occ = new Array(n);

  let current = 0;
  for (let i = 0; i < n; i++) {
    const inV = Number(ingressFlow?.[i] ?? 0);
    const outV = Number(egressFlow?.[i] ?? 0);
    current += (inV - outV);
    occ[i] = clamp0 ? Math.max(0, current) : current;
  }

  if (zeroEnd && n > 0) {
    const offset = occ[n - 1];
    for (let i = 0; i < n; i++) occ[i] = occ[i] - offset;
    if (clamp0) for (let i = 0; i < n; i++) occ[i] = Math.max(0, occ[i]);
  }

  return occ;
}
